import React, { useMemo, useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import {
    useProperty,
    usePropertyComplementaryData,
    usePropertyFetching,
    usePropertyImagesDetail,
} from '../../../contextsProperty/ContextProperties';
import Card from '../../../../muiComponents/Card';
import GridChild from '../../../../muiComponents/GridChild';
import Grid from '../../../../muiComponents/Grid';
import Menu from '../../components/Menu';
import useTranslate from '../../../../customHooks/useTranslate';
import ImageCard from './ImageCard';
import Button from '../../../../muiComponents/Button';
import AddImagesPanel from './AddImagesPanel';
import DeletePopUp from '../DeletePopUp';
import Icon from '../../../../muiComponents/Icon';
import {
    BEDROOM_IMAGE_TYPE_KEY,
    imageSmallColumnGrid,
    URL_HOTEL_BASE,
    URL_INVENTORY_LIST,
} from '../../../../utils/property/propertyUtils';
import { ComplementaryImageTypeState } from '../../../../types/properties/propertyTypes';
import LoadingTab from '../LoadingTab';
import Title from '../Title';
import {
    gridSpacing30,
    gridSpacing40,
} from '../../../../utils/commonVariables/grid';

const ImagesList = () => {
    const translate = useTranslate();
    const { hotelImagesTypeList } = usePropertyComplementaryData();
    const {
        id,
        images,
        toggleFeaturedImage,
        toggleImage360,
        handleDeleteImage,
    } = useProperty();
    const { isOpenPanel, setAddImageDetail } = usePropertyImagesDetail();
    const { isFetching } = usePropertyFetching();

    const [idDeletePopUpVisible, setIdDeletePopUpVisible] = useState('');

    const featureImage = useMemo(() => {
        return images.find(image => image.feature);
    }, [images]);

    if (isFetching) {
        return <LoadingTab />;
    }

    return (
        <>
            <main className='property-page__content flex-column gap-20'>
                <div className='flex flex-end flex-align-center gap-20'>
                    {/* <SearchBar placeholder='search' value='' onChange={() => {}} /> */}
                    <div className='flex-grow'>
                        <Title
                            as='h1'
                            text={'images_title'}
                            className='width100perc text-align-center'
                        />
                    </div>
                    <Button
                        variant='contained'
                        onClick={() => {
                            setAddImageDetail(null);
                        }}
                    >
                        {translate('add_images')}
                    </Button>
                </div>
                {!featureImage ? (
                    <Card className='padding20'>
                        <Alert severity='info'>
                            <AlertTitle sx={{ fontWeight: 'bold' }}>
                                {translate('fav_image_alert_title')}
                            </AlertTitle>
                            <p style={{ fontWeight: 'normal' }}>
                                {translate('fav_image_alert_content')}
                            </p>
                        </Alert>
                    </Card>
                ) : (
                    ''
                )}
                {hotelImagesTypeList.map(imageType => {
                    const isRoomSection =
                        imageType.nameKey === BEDROOM_IMAGE_TYPE_KEY;

                    const sectionImages = images.filter(
                        img => img.idType === parseInt(imageType.id)
                    );

                    return (
                        <section
                            key={imageType.id}
                            className='flex-column gap-10'
                        >
                            <div className='flex flex-between flex-align-center'>
                                <Title
                                    text={imageType.name}
                                    translated={false}
                                />
                                {isRoomSection ? (
                                    ''
                                ) : (
                                    <Menu
                                        menuItems={[
                                            {
                                                text: 'add_images',
                                                icon: 'AddPhotoAlternate',
                                                onClick: () => {
                                                    setAddImageDetail({
                                                        ...imageType,
                                                        label: imageType.name,
                                                    } as ComplementaryImageTypeState);
                                                },
                                            },
                                        ]}
                                    />
                                )}
                            </div>

                            <Card className='padding20 flex-column gap-20'>
                                {isRoomSection ? (
                                    <Alert
                                        severity='info'
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                        action={
                                            <div
                                                className='flex flex-align-center gap-10 margin-inline10 cursor-pointer'
                                                onClick={() => {
                                                    const url = `${URL_HOTEL_BASE(
                                                        id
                                                    )}${URL_INVENTORY_LIST}`;
                                                    window.open(url, url);
                                                }}
                                            >
                                                <Icon iconName='OpenInNew' />
                                                <p className=''>
                                                    {translate(
                                                        'go_to_room_inventory'
                                                    )}
                                                </p>
                                            </div>
                                        }
                                    >
                                        <div className='flex flex-between width100perc'>
                                            <p className='font-weight-b'>
                                                {translate('rooms_images_text')}
                                            </p>
                                        </div>
                                    </Alert>
                                ) : (
                                    ''
                                )}
                                {sectionImages.length ? (
                                    <Grid
                                        columnSpacing={gridSpacing30}
                                        rowSpacing={gridSpacing40}
                                        style={{ width: 'unset' }}
                                    >
                                        {sectionImages.map((img, ind) => (
                                            <GridChild
                                                gridProps={imageSmallColumnGrid}
                                                key={img?.id || ind}
                                            >
                                                <ImageCard
                                                    image={img}
                                                    onDelete={() =>
                                                        setIdDeletePopUpVisible(
                                                            img.id
                                                        )
                                                    }
                                                    onToggleFeatured={
                                                        toggleFeaturedImage
                                                    }
                                                    onToggle360={toggleImage360}
                                                    isToolbarVisible={
                                                        !isRoomSection
                                                    }
                                                    isDeleteButtonVisible={
                                                        !isRoomSection
                                                    }
                                                />
                                            </GridChild>
                                        ))}
                                    </Grid>
                                ) : isRoomSection ? (
                                    ''
                                ) : (
                                    <Button
                                        variant='text'
                                        startIcon={'Add'}
                                        onClick={() =>
                                            setAddImageDetail({
                                                ...imageType,
                                                label: imageType.name,
                                            } as ComplementaryImageTypeState)
                                        }
                                    >
                                        {translate('add_images')}
                                    </Button>
                                )}
                            </Card>
                        </section>
                    );
                })}
            </main>
            <AddImagesPanel open={isOpenPanel} />
            <DeletePopUp
                open={!!idDeletePopUpVisible}
                onSave={() => {
                    handleDeleteImage(idDeletePopUpVisible);
                    setIdDeletePopUpVisible('');
                }}
                onCancel={() => {
                    setIdDeletePopUpVisible('');
                }}
            />
        </>
    );
};

export default ImagesList;
